import { useEffect, useState, Fragment } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, PackageSearch, RefreshCw, ChevronDown, ChevronRight, Eye } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

const zl = (n) => `${(n || 0).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;

export const CommissionGaps = ({ onAddTiers, onOpenOrder, reloadSignal }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());

  const load = () => {
    setLoading(true);
    api.get("/products-commission-gaps")
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [reloadSignal]);

  const toggle = (sku) => setExpanded((s) => { const n = new Set(s); n.has(sku) ? n.delete(sku) : n.add(sku); return n; });

  return (
    <div data-testid="comm-gaps-view" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="font-mono text-xs font-bold uppercase tracking-widest text-amber-400">Produkty-prowizja</p>
          <p className="text-sm font-medium text-muted-foreground">Produkty sprzedane bez naliczonej prowizji (ustalenie indywidualne). Rozwiń produkt, aby zobaczyć zamówienia i klientów.</p>
        </div>
        <Button data-testid="comm-gaps-refresh" variant="outline" size="sm" className="gap-2 font-semibold" onClick={load}><RefreshCw className="h-4 w-4" />Odśwież</Button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-muted-foreground">Ładowanie…</div>
      ) : rows.length === 0 ? (
        <div data-testid="comm-gaps-empty" className="rounded-2xl border border-dashed border-emerald-500/40 bg-emerald-400/[0.05] p-12 text-center">
          <PackageSearch className="mx-auto mb-3 h-8 w-8 text-emerald-400" />
          <p className="font-display text-lg font-extrabold">Wszystko rozliczone 🎉</p>
          <p className="text-sm font-medium text-muted-foreground">Każdy sprzedany produkt ma naliczoną prowizję.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]" data-testid="comm-gaps-table">
            <thead className="text-muted-foreground font-mono text-[11px] uppercase tracking-wider">
              <tr className="border-b border-border">
                <th className="text-left px-4 py-3">Produkt</th>
                <th className="text-right px-3 py-3">Sprzedano</th>
                <th className="text-right px-3 py-3">Zamówienia</th>
                <th className="text-right px-3 py-3">Wartość netto</th>
                <th className="text-left px-3 py-3">Progi</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const open = expanded.has(r.sku);
                return (
                  <Fragment key={r.sku}>
                    <tr data-testid={`comm-gap-row-${r.sku}`} className="border-t border-border hover:bg-accent/40">
                      <td className="px-4 py-3">
                        <div className="font-mono text-xs font-bold text-amber-300">{r.sku}</div>
                        <div className="font-semibold">{r.name}</div>
                        {r.type && <div className="text-xs text-muted-foreground">{r.type}</div>}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums font-bold">{r.qty} szt.</td>
                      <td className="px-3 py-3 text-right">
                        <button data-testid={`comm-gap-toggle-${r.sku}`} onClick={() => toggle(r.sku)} className="inline-flex items-center gap-1 rounded-md px-2 py-1 font-semibold tabular-nums hover:bg-accent transition-colors">
                          {open ? <ChevronDown className="h-4 w-4 text-amber-400" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                          {r.orders}
                        </button>
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums font-semibold">{zl(r.net)}</td>
                      <td className="px-3 py-3">
                        {r.tiers_count > 0 ? (
                          <span className="inline-flex items-center gap-1 rounded bg-amber-400/15 px-2 py-0.5 text-[11px] font-bold text-amber-300">
                            <AlertTriangle className="h-3 w-3" />{r.tiers_count} progi — cena poniżej
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded bg-rose-500/15 px-2 py-0.5 text-[11px] font-bold text-rose-300">
                            <AlertTriangle className="h-3 w-3" />Brak progów
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button data-testid={`comm-gap-add-${r.sku}`} size="sm" className="gap-1.5 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold whitespace-nowrap"
                          onClick={() => onAddTiers(r.sku)}>
                          Dodaj progi<ArrowRight className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                    {open && (
                      <tr data-testid={`comm-gap-orders-${r.sku}`} className="bg-secondary/30">
                        <td colSpan={6} className="px-4 py-3">
                          <div className="space-y-1.5">
                            {(r.order_list || []).map((o) => (
                              <div key={o.id} data-testid={`comm-gap-order-${r.sku}-${o.id}`} className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2">
                                <span className="font-mono text-xs font-bold text-amber-300 w-28 shrink-0">{o.number}</span>
                                <span className="font-medium flex-1 truncate">{o.customer_name || "—"}</span>
                                <span className="text-xs text-muted-foreground tabular-nums shrink-0">{o.qty} szt.</span>
                                <Button data-testid={`comm-gap-open-${o.id}`} size="sm" variant="outline" className="gap-1.5 font-semibold shrink-0"
                                  onClick={() => onOpenOrder && onOpenOrder(o.id)}>
                                  <Eye className="h-4 w-4" />Zobacz
                                </Button>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
